import { PHONE_DIALPAD_KEYS } from '@/constants/phoneDialpad';
import { ActionIcon, Box, Button, Group, Stack, Text, rem } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';

const PRESS_MS = 150;

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
    return true;
  }
  return target.isContentEditable;
}

function keyboardEventToDigit(e: KeyboardEvent): string | null {
  if (e.repeat || e.ctrlKey || e.metaKey || e.altKey) {
    return null;
  }
  if (/^[0-9]$/.test(e.key)) {
    return e.key;
  }
  if (e.key === '*' || e.code === 'NumpadMultiply') {
    return '*';
  }
  if (e.key === '#') {
    return '#';
  }
  return null;
}

export interface InCallDialpadProps {
  /** Full DTMF sequence for this call (caller-owned so it survives keypad toggle). */
  enteredDigits: string;
  onEnteredDigitsClear: () => void;
  onDigit: (digit: string) => void;
}

export function InCallDialpad({ enteredDigits, onEnteredDigitsClear, onDigit }: InCallDialpadProps): ReactElement {
  const [pressingKey, setPressingKey] = useState<string | null>(null);
  const pressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flashKey = useCallback((digit: string) => {
    if (pressTimeoutRef.current) {
      clearTimeout(pressTimeoutRef.current);
    }
    setPressingKey(digit);
    pressTimeoutRef.current = setTimeout(() => {
      setPressingKey(null);
      pressTimeoutRef.current = null;
    }, PRESS_MS);
  }, []);

  const handleDigit = useCallback(
    (digit: string) => {
      onDigit(digit);
      flashKey(digit);
    },
    [flashKey, onDigit]
  );

  useEffect(() => {
    return () => {
      if (pressTimeoutRef.current) {
        clearTimeout(pressTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) {
        return;
      }
      const digit = keyboardEventToDigit(e);
      if (digit == null) {
        return;
      }
      e.preventDefault();
      handleDigit(digit);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleDigit]);

  return (
    <Box mt="sm" p="sm" bdrs={rem(16)} bg="rgba(0, 0, 0, 0.25)">
      <Group justify="space-between" align="center" gap="xs" mb="xs" wrap="nowrap">
        <Text
          size="sm"
          c="gray.3"
          style={{
            flex: 1,
            fontFamily: 'monospace',
            letterSpacing: rem(4),
            minHeight: rem(22),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          aria-live="polite"
        >
          {enteredDigits || ' '}
        </Text>

        <ActionIcon
          variant="subtle"
          color="gray.4"
          size="sm"
          aria-label="Clear entered tones"
          onClick={(ev) => {
            ev.stopPropagation();
            onEnteredDigitsClear();
          }}
          onMouseDown={(ev) => ev.stopPropagation()}
        >
          <IconX size={16} />
        </ActionIcon>
      </Group>

      <Box
        display="grid"
        style={{
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: rem(6),
          textAlign: 'center',
        }}
      >
        {PHONE_DIALPAD_KEYS.map((item) => {
          const isPressed = pressingKey === item.label;
          return (
            <Button
              key={item.label}
              variant="subtle"
              size="sm"
              radius="md"
              h={56}
              data-active={isPressed ? true : undefined}
              bg={isPressed ? 'rgba(255, 255, 255, 0.15)' : undefined}
              style={{
                transition: 'background-color 0.08s ease',
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onTouchStart={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDigit(item.label);
              }}
            >
              <Stack gap={0} align="center" justify="center" w="100%">
                <Text fz="lg" c="white">
                  {item.label}
                </Text>
                <Text fz="xs" c="gray.5" lh={1}>
                  {item.sub}
                </Text>
              </Stack>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
}
